import { useState } from 'react';
import type { Session } from '../backend/types.ts';
import { activityLabel } from '../lib/activities.ts';
import { groupNames } from '../lib/catalog.ts';
import { dateTime, minutesBetween } from '../lib/format.ts';
import { useModel } from '../state.tsx';
import { Card, Empty, Pill, ratingColor } from '../ui/components.tsx';
import { LogSession } from './LogSession.tsx';

/** Monday of the week containing d, as YYYY-MM-DD (local). */
function weekStart(iso: string): string {
  const d = new Date(iso);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d.toISOString();
}

export function History() {
  const model = useModel();
  const [editing, setEditing] = useState<Session | null>(null);
  const weeks = new Map<string, Session[]>();
  for (const s of model.logged) weeks.set(weekStart(s.started_at), [...(weeks.get(weekStart(s.started_at)) ?? []), s]);
  const thisWeek = weekStart(new Date().toISOString());

  return (
    <div className="screen">
      <h1>History</h1>
      {!model.logged.length && <Empty title="No sessions yet" detail="Tap “Log a session” on Today after you train." />}
      {[...weeks].map(([start, sessions]) => (
        <div className="stack" key={start}>
          <span className="eyebrow">{start === thisWeek ? 'This week' : `Week of ${new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(new Date(start))}`}</span>
          <Card>
            <div className="list">
              {sessions.map((s) => (
                <button key={s.id} className="list-item" onClick={() => setEditing(s)} data-testid="history-row">
                  <div className="stack" style={{ gap: 4, flex: 1 }}>
                    <div className="row">
                      <h2 style={{ fontSize: 16 }}>{s.kind === 'trainer' ? 'Session with trainer' : activityLabel(s.activity_type)}</h2>
                      <span className="spacer" />
                      <span className="caption">{dateTime(s.started_at)}{minutesBetween(s.started_at, s.ended_at) ? ` · ${minutesBetween(s.started_at, s.ended_at)} min` : ''}</span>
                    </div>
                    <span className="caption">{groupNames(s.muscles.map((m) => m.muscle_id)).join(' · ')}</span>
                    <div className="pills">
                      {s.intensity && <Pill text={`Intensity ${s.intensity}`} color={ratingColor(6 - s.intensity)} />}
                      {s.recovery && <Pill text={`Recovery ${s.recovery}`} color={ratingColor(s.recovery)} />}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </Card>
        </div>
      ))}
      {editing && <LogSession session={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
